import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { api, UPLOADS_BASE, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../../components/ui/accordion";
import {
  Pencil, Trash2, Plus, Link2, Upload, FileText, RefreshCcw, Wrench,
  ShoppingCart, Paperclip, Phone, Mail, MapPin, FileDown, Image as ImageIcon, File as FileIcon,
  Share2, Store, ShieldOff, Download, History, Star, Search,
} from "lucide-react";
import { toast } from "sonner";

const MODULE_META = {
  devis: { label: "Devis", icon: FileText, path: "devis" },
  reprise: { label: "Reprise", icon: RefreshCcw, path: "reprises" },
  intervention: { label: "Intervention", icon: Wrench, path: "interventions" },
  caisse: { label: "Ticket / Facture", icon: ShoppingCart, path: "caisse" },
};

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [folder, setFolder] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkModule, setLinkModule] = useState("devis");
  const [linkOptions, setLinkOptions] = useState([]);
  const [linkChoice, setLinkChoice] = useState("");
  const [linkSearch, setLinkSearch] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [allShops, setAllShops] = useState([]);
  const [shareSelection, setShareSelection] = useState([]);
  const [accessLogOpen, setAccessLogOpen] = useState(false);
  const [accessLog, setAccessLog] = useState([]);
  const fileInputRef = useRef(null);
  const [pendingUploadDoc, setPendingUploadDoc] = useState(null); // {module, id} or null = client-level

  const canEdit = hasPerm(user, "client", "edit");
  const canDelete = hasPerm(user, "client", "delete");
  const canFidelite = hasPerm(user, "client", "fidelite_ajustement");
  // Gestion des fiches/documents (lier une fiche, ajouter/retirer un fichier
  // interne ou externe) : permission dédiée "add_document", indépendante du
  // droit de modifier les informations personnelles du client (nom, prénom,
  // téléphone, email, adresse). Ceux qui ont "edit" gardent naturellement accès.
  const canDocs = canEdit || hasPerm(user, "client", "add_document");

  const load = async () => {
    try {
      const { data } = await api.get(`/clients/${id}/folder`);
      setFolder(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cartes cadeaux obtenues via le programme de fidélité (voir
  // carte_cadeau_utils.py) — affichées sur la fiche, quel que soit le module
  // "documents" classique.
  const [cartesCadeaux, setCartesCadeaux] = useState([]);
  useEffect(() => {
    api.get("/caisse/cartes-cadeaux", { params: { client_id: id } })
      .then((r) => setCartesCadeaux(r.data))
      .catch(() => setCartesCadeaux([]));
  }, [id]);

  // Rectification manuelle du solde de points fidélité — réservée aux
  // utilisateurs disposant explicitement de la permission dédiée (voir
  // routers/client_routes.py, POST /clients/{id}/fidelite-ajustement).
  const [fideliteAjustDialogOpen, setFideliteAjustDialogOpen] = useState(false);
  const [fideliteAjustDelta, setFideliteAjustDelta] = useState("");
  const [fideliteAjustMotif, setFideliteAjustMotif] = useState("");
  const [fideliteAjustSubmitting, setFideliteAjustSubmitting] = useState(false);
  const [fideliteHistorique, setFideliteHistorique] = useState([]);
  const [fideliteHistoriqueOpen, setFideliteHistoriqueOpen] = useState(false);

  const loadFideliteHistorique = async () => {
    try {
      const { data } = await api.get(`/clients/${id}/fidelite-ajustements`);
      setFideliteHistorique(data);
    } catch {
      setFideliteHistorique([]);
    }
  };

  const submitFideliteAjustement = async () => {
    const delta = Number(fideliteAjustDelta);
    if (!delta) { toast.error("Indiquez un ajustement non nul"); return; }
    if (!fideliteAjustMotif.trim()) { toast.error("Le motif est obligatoire"); return; }
    setFideliteAjustSubmitting(true);
    try {
      const { data } = await api.post(`/clients/${id}/fidelite-ajustement`, { delta, motif: fideliteAjustMotif.trim() });
      toast.success(`Solde mis à jour : ${data.fidelite_points} points`);
      setFideliteAjustDialogOpen(false);
      setFideliteAjustDelta("");
      setFideliteAjustMotif("");
      load();
      if (fideliteHistoriqueOpen) loadFideliteHistorique();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setFideliteAjustSubmitting(false);
    }
  };


  const openEdit = () => { setForm(folder.client); setEditOpen(true); };

  const saveEdit = async () => {
    try {
      await api.patch(`/clients/${id}`, form);
      toast.success("Fiche client mise à jour");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const removeClient = async () => {
    if (!window.confirm("Supprimer ce client ? Les fiches et tickets liés ne seront pas supprimés, seulement détachés.")) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client supprimé");
      navigate("/clients");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const anonymizeClient = async () => {
    if (!window.confirm(
      "Anonymiser ce client (RGPD) ? Son nom, téléphone, email et adresse seront effacés de sa fiche. "
      + "Les tickets et factures déjà émis à son nom ne sont PAS modifiés : ils doivent légalement rester "
      + "complets pendant la durée de conservation fiscale. Cette action est irréversible."
    )) return;
    try {
      await api.post(`/clients/${id}/anonymize`);
      toast.success("Fiche client anonymisée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const exportClientData = async () => {
    try {
      const { data } = await api.get(`/clients/${id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `donnees-personnelles-${(client?.nom || id)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export RGPD téléchargé");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openAccessLog = async () => {
    try {
      const { data } = await api.get(`/clients/${id}/access-log`);
      setAccessLog(data);
      setAccessLogOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const createLinkedDoc = (module) => {
    const c = folder.client;
    const params = new URLSearchParams({
      client_id: c.id,
      client_nom: `${c.prenom || ""} ${c.nom}`.trim(),
      client_tel: c.telephone || "",
      client_email: c.email || "",
      client_adresse: c.adresse || "",
    });
    navigate(`/${MODULE_META[module].path}/new?${params.toString()}`);
  };

  const openLinkExisting = async (module) => {
    setLinkModule(module);
    setLinkChoice("");
    setLinkSearch("");
    try {
      const { data } = await api.get(`/${MODULE_META[module].path}`);
      setLinkOptions(data.filter((d) => d.can_open !== false && !d.client_id));
      setLinkOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filteredLinkOptions = linkOptions.filter((o) => {
    const q = linkSearch.trim().toLowerCase();
    if (!q) return true;
    const nom = (o.client_info?.nom || o.client_nom || "").toLowerCase();
    return (o.numero || "").toLowerCase().includes(q) || nom.includes(q);
  });

  const confirmLink = async () => {
    if (!linkChoice) { toast.error("Choisissez une fiche à lier"); return; }
    try {
      await api.post(`/clients/${id}/link-document`, { module: linkModule, document_id: linkChoice });
      toast.success("Fiche liée au client");
      setLinkOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openShare = async () => {
    try {
      const { data } = await api.get("/shops");
      setAllShops(data.filter((s) => s.type === "boutique" && s.id !== client.shop_id));
      setShareSelection(client.shared_shop_ids || []);
      setShareOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleShareShop = (shopId) => {
    setShareSelection((prev) =>
      prev.includes(shopId) ? prev.filter((id) => id !== shopId) : [...prev, shopId]
    );
  };

  const saveShare = async () => {
    try {
      await api.post(`/clients/${id}/share-shops`, { shop_ids: shareSelection });
      toast.success("Fiche client partagée");
      setShareOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const unlinkDoc = async (module, docId) => {
    try {
      await api.delete(`/clients/${id}/link-document`, { params: { module, document_id: docId } });
      toast.success("Fiche détachée du client");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const triggerUpload = (module, docId) => {
    setPendingUploadDoc(module ? { module, id: docId } : null);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (pendingUploadDoc) {
      fd.append("module", pendingUploadDoc.module);
      fd.append("document_id", pendingUploadDoc.id);
    }
    try {
      await api.post(`/clients/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Fichier ajouté");
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    }
  };

  const removeFile = async (fileId) => {
    try {
      await api.delete(`/clients/${id}/files/${fileId}`);
      toast.success("Fichier supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!hasPerm(user, "client", "view")) return <Navigate to="/" replace />;
  if (!folder) return <Layout title="Client"><p className="text-muted-foreground">Chargement...</p></Layout>;

  const { client, documents, files } = folder;
  const totalDocs = Object.values(documents).reduce((s, arr) => s + arr.length, 0);

  return (
    <Layout title={`${client.prenom || ""} ${client.nom}`.trim()}>
      <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileSelected} data-testid="client-file-input" />

      <div className="rounded-xl border border-border bg-card p-5 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl font-bold" data-testid="client-detail-name">{client.prenom} {client.nom}</h2>
            {client.anonymized && (
              <Badge variant="secondary" className="mt-1.5 gap-1.5" data-testid="client-anonymized-badge">
                <ShieldOff size={12} /> Fiche anonymisée (RGPD)
              </Badge>
            )}
            {(client.fidelite_points > 0 || canFidelite) && (
              <div className="flex items-center gap-2 mt-1.5" data-testid="client-fidelite-row">
                <Badge variant="secondary" className="gap-1.5" data-testid="client-fidelite-badge">
                  <Star size={12} /> {client.fidelite_points || 0} points fidélité
                </Badge>
                {canFidelite && (
                  <button
                    className="text-xs text-primary hover:underline"
                    onClick={() => { setFideliteAjustDialogOpen(true); setFideliteAjustDelta(""); setFideliteAjustMotif(""); }}
                    data-testid="client-fidelite-ajust-button"
                  >
                    Rectifier
                  </button>
                )}
                {canFidelite && (
                  <button
                    className="text-xs text-muted-foreground hover:underline"
                    onClick={() => { setFideliteHistoriqueOpen(true); loadFideliteHistorique(); }}
                    data-testid="client-fidelite-historique-button"
                  >
                    Historique
                  </button>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
              {client.telephone && <span className="flex items-center gap-1.5"><Phone size={13} /> {client.telephone}</span>}
              {client.email && <span className="flex items-center gap-1.5"><Mail size={13} /> {client.email}</span>}
              {client.adresse && <span className="flex items-center gap-1.5"><MapPin size={13} /> {client.adresse}</span>}
            </div>
            {client.notes && <p className="text-sm mt-2">{client.notes}</p>}
            {!client.is_owner && client.shared_by_shop_name && (
              <Badge variant="secondary" className="mt-2 gap-1.5" data-testid="client-shared-badge">
                <Share2 size={12} /> Partagé par {client.shared_by_shop_name}
              </Badge>
            )}
            {client.is_owner && client.shared_shop_names?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="client-shared-with-list">
                {client.shared_shop_names.map((n, i) => (
                  <Badge key={i} variant="secondary" className="gap-1.5"><Store size={12} /> {n}</Badge>
                ))}
              </div>
            )}
            {cartesCadeaux.length > 0 && (
              <div className="mt-3" data-testid="client-cartes-cadeaux">
                <p className="text-xs font-semibold text-muted-foreground mb-1.5">Cartes cadeaux</p>
                <div className="flex flex-wrap gap-2">
                  {cartesCadeaux.map((c) => (
                    <a
                      key={c.id}
                      href={`${api.defaults.baseURL}/caisse/cartes-cadeaux/${c.id}/pdf`}
                      target="_blank" rel="noreferrer"
                      className={`flex items-center gap-1.5 text-xs rounded-lg border px-2.5 py-1.5 hover:bg-accent transition-colors ${c.expiree ? "border-destructive/40 text-destructive" : "border-border"}`}
                      data-testid={`client-carte-cadeau-${c.id}`}
                    >
                      <Star size={12} />
                      <span className="font-semibold">{c.montant.toFixed(2)} €</span>
                      <span className="font-mono text-muted-foreground">{c.code}</span>
                      {c.expiree && <span>(expirée)</span>}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" className="gap-2" data-testid="client-export-rgpd-button" onClick={exportClientData}>
              <Download size={14} /> Exporter mes données (RGPD)
            </Button>
            {canEdit && (
              <Button size="sm" variant="outline" className="gap-2" data-testid="client-access-log-button" onClick={openAccessLog}>
                <History size={14} /> Journal d'accès
              </Button>
            )}
            {canEdit && client.is_owner && (
              <Button size="sm" variant="outline" className="gap-2" data-testid="client-share-button" onClick={openShare}>
                <Share2 size={14} /> Partager à une boutique
              </Button>
            )}
            {canEdit && (
              <Button size="sm" variant="outline" className="gap-2" data-testid="client-edit-button" onClick={openEdit}>
                <Pencil size={14} /> Modifier
              </Button>
            )}
            {canDelete && !client.anonymized && (
              <Button size="sm" variant="outline" className="gap-2" data-testid="client-anonymize-button" onClick={anonymizeClient}>
                <ShieldOff size={14} /> Anonymiser (RGPD)
              </Button>
            )}
            {canDelete && (
              <Button size="sm" variant="outline" className="gap-2 text-destructive" data-testid="client-delete-button" onClick={removeClient}>
                <Trash2 size={14} /> Supprimer
              </Button>
            )}
          </div>
        </div>
      </div>

      {canDocs && (
        <div className="rounded-xl border border-border bg-card p-5 mb-6">
          <p className="font-heading font-semibold mb-3">Ajouter au dossier</p>
          <div className="flex flex-wrap gap-2">
            {["devis", "reprise", "intervention"].map((m) => {
              const Meta = MODULE_META[m];
              return (
                <Button key={m} size="sm" variant="outline" className="gap-2" data-testid={`client-create-${m}`} onClick={() => createLinkedDoc(m)}>
                  <Plus size={14} /> Nouvelle {Meta.label.toLowerCase()}
                </Button>
              );
            })}
            {["devis", "reprise", "intervention", "caisse"].map((m) => (
              <Button key={`link-${m}`} size="sm" variant="ghost" className="gap-2" data-testid={`client-link-${m}`} onClick={() => openLinkExisting(m)}>
                <Link2 size={14} /> Lier une fiche {MODULE_META[m].label.toLowerCase()} existante
              </Button>
            ))}
            <Button size="sm" variant="ghost" className="gap-2" data-testid="client-upload-general" onClick={() => triggerUpload(null, null)}>
              <Upload size={14} /> Ajouter un fichier (photo, PDF...)
            </Button>
          </div>
        </div>
      )}

      {files.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5 mb-6" data-testid="client-general-files">
          <p className="font-heading font-semibold mb-3 flex items-center gap-2"><Paperclip size={15} /> Fichiers du client</p>
          <div className="flex flex-wrap gap-2">
            {files.map((f) => <FileChip key={f.id} file={f} onRemove={canDocs ? () => removeFile(f.id) : null} />)}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-5">
        <p className="font-heading font-semibold mb-3">Documents liés ({totalDocs})</p>
        {totalDocs === 0 && <p className="text-sm text-muted-foreground">Aucun document lié pour le moment.</p>}
        <Accordion type="multiple" className="space-y-2">
          {Object.entries(documents).flatMap(([module, docs]) =>
            docs.map((d) => {
              const Meta = MODULE_META[module];
              const Icon = Meta.icon;
              return (
                <AccordionItem key={`${module}-${d.id}`} value={`${module}-${d.id}`} className="border border-border rounded-lg px-3" data-testid={`client-doc-${module}-${d.id}`}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2 text-sm">
                      <Icon size={15} className="text-muted-foreground" />
                      <span className="font-medium">{Meta.label}</span>
                      <span className="text-muted-foreground">{d.numero}</span>
                      {d.files?.length > 0 && <Badge variant="secondary">{d.files.length} fichier(s)</Badge>}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-wrap items-center gap-3 mb-3 text-sm">
                      {module === "caisse" ? (
                        <a href={`${api.defaults.baseURL}/${Meta.path}/${d.id}/pdf`} target="_blank" rel="noreferrer" className="text-primary flex items-center gap-1" data-testid={`client-doc-open-${module}-${d.id}`}>
                          <FileDown size={13} /> Ouvrir le PDF
                        </a>
                      ) : (
                        <Link to={`/${Meta.path}/${d.id}`} className="text-primary" data-testid={`client-doc-open-${module}-${d.id}`}>
                          Ouvrir la fiche
                        </Link>
                      )}
                      {module !== "caisse" && (
                        <a href={`${api.defaults.baseURL}/${Meta.path}/${d.id}/pdf`} target="_blank" rel="noreferrer" className="text-muted-foreground flex items-center gap-1">
                          <FileDown size={13} /> PDF
                        </a>
                      )}
                      {canDocs && (
                        <button className="text-muted-foreground flex items-center gap-1" data-testid={`client-doc-upload-${module}-${d.id}`} onClick={() => triggerUpload(module, d.id)}>
                          <Upload size={13} /> Ajouter photo/PDF
                        </button>
                      )}
                      {canDocs && (
                        <button className="text-destructive flex items-center gap-1" data-testid={`client-doc-unlink-${module}-${d.id}`} onClick={() => unlinkDoc(module, d.id)}>
                          Détacher du client
                        </button>
                      )}
                    </div>
                    {d.files?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {d.files.map((f) => <FileChip key={f.id} file={f} onRemove={canDocs ? () => removeFile(f.id) : null} />)}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">Aucun fichier attaché à cette fiche.</p>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })
          )}
        </Accordion>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="client-edit-dialog">
          <DialogHeader><DialogTitle>Modifier la fiche client</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="client-edit-nom" placeholder="Nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
                <Input data-testid="client-edit-prenom" placeholder="Prénom" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} />
              </div>
              <Input data-testid="client-edit-telephone" placeholder="Téléphone" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
              <Input data-testid="client-edit-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input data-testid="client-edit-adresse" placeholder="Adresse postale" value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} />
              <Textarea data-testid="client-edit-notes" placeholder="Notes (optionnel)" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          )}
          <DialogFooter><Button data-testid="client-edit-save" onClick={saveEdit}>Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent data-testid="client-link-dialog">
          <DialogHeader><DialogTitle>Lier une fiche {MODULE_META[linkModule]?.label.toLowerCase()} existante</DialogTitle></DialogHeader>
          <div className="relative mb-1">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Rechercher par numéro ou nom du client..."
              value={linkSearch}
              onChange={(e) => setLinkSearch(e.target.value)}
              className="pl-8"
              data-testid="client-link-search"
            />
          </div>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid="client-link-results">
            {linkOptions.length === 0 && (
              <p className="px-3 py-3 text-sm text-muted-foreground">Aucune fiche non rattachée disponible</p>
            )}
            {linkOptions.length > 0 && filteredLinkOptions.length === 0 && (
              <p className="px-3 py-3 text-sm text-muted-foreground">Aucun résultat pour cette recherche</p>
            )}
            {filteredLinkOptions.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setLinkChoice(o.id)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors ${linkChoice === o.id ? "bg-accent" : ""}`}
                data-testid={`client-link-option-${o.id}`}
              >
                <span className="font-medium">{o.numero}</span>
                <span className="text-muted-foreground"> — {o.client_info?.nom || o.client_nom || "—"}</span>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button data-testid="client-link-confirm" onClick={confirmLink} disabled={!linkChoice}>Lier au client</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="client-share-dialog">
          <DialogHeader><DialogTitle>Partager la fiche à une ou plusieurs boutiques</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {allShops.length === 0 && (
              <p className="text-sm text-muted-foreground">Aucune autre boutique disponible.</p>
            )}
            {allShops.map((s) => (
              <label
                key={s.id}
                htmlFor={`share-shop-${s.id}`}
                className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-accent cursor-pointer"
                data-testid={`client-share-shop-${s.id}`}
              >
                <Checkbox
                  id={`share-shop-${s.id}`}
                  checked={shareSelection.includes(s.id)}
                  onCheckedChange={() => toggleShareShop(s.id)}
                />
                <Store size={14} className="text-muted-foreground" />
                <span className="text-sm">{s.nom}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button data-testid="client-share-save" onClick={saveShare}>Enregistrer le partage</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={accessLogOpen} onOpenChange={setAccessLogOpen}>
        <DialogContent data-testid="client-access-log-dialog">
          <DialogHeader>
            <DialogTitle>Journal d'accès à cette fiche (RGPD — art. 5.2)</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {accessLog.length === 0 && (
              <p className="text-sm text-muted-foreground">Aucun accès enregistré pour le moment.</p>
            )}
            {accessLog.map((e) => (
              <div key={e.id} className="flex items-center justify-between text-sm border-b border-border pb-1.5" data-testid={`client-access-log-row-${e.id}`}>
                <span>{e.accessed_by || "—"}</span>
                <span className="text-muted-foreground">{e.action}</span>
                <span className="text-xs text-muted-foreground">{e.accessed_at ? new Date(e.accessed_at).toLocaleString("fr-FR") : "—"}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={fideliteAjustDialogOpen} onOpenChange={setFideliteAjustDialogOpen}>
        <DialogContent data-testid="client-fidelite-ajust-dialog">
          <DialogHeader><DialogTitle>Rectifier le solde de points fidélité</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground mb-2">
            Solde actuel : <strong>{client.fidelite_points || 0} points</strong>. Indiquez un ajustement positif
            (ajout) ou négatif (retrait) — un motif est obligatoire et conservé dans l'historique.
          </p>
          <Input
            type="number"
            placeholder="Ajustement (ex. 50 ou -20)"
            value={fideliteAjustDelta}
            onChange={(e) => setFideliteAjustDelta(e.target.value)}
            className="mb-2"
            autoFocus
            data-testid="client-fidelite-ajust-delta"
          />
          <Textarea
            placeholder="Motif (obligatoire) — ex. geste commercial, correction d'erreur..."
            value={fideliteAjustMotif}
            onChange={(e) => setFideliteAjustMotif(e.target.value)}
            rows={3}
            data-testid="client-fidelite-ajust-motif"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFideliteAjustDialogOpen(false)}>Annuler</Button>
            <Button onClick={submitFideliteAjustement} disabled={fideliteAjustSubmitting} data-testid="client-fidelite-ajust-submit">
              {fideliteAjustSubmitting ? "Enregistrement..." : "Valider la rectification"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={fideliteHistoriqueOpen} onOpenChange={setFideliteHistoriqueOpen}>
        <DialogContent data-testid="client-fidelite-historique-dialog">
          <DialogHeader><DialogTitle>Historique des rectifications de points</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {fideliteHistorique.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="client-fidelite-historique-empty">Aucune rectification manuelle pour l'instant.</p>
            )}
            {fideliteHistorique.map((h, i) => (
              <div key={i} className="border-b border-border pb-2 text-sm" data-testid={`client-fidelite-historique-row-${i}`}>
                <div className="flex items-center justify-between">
                  <span className={`font-semibold ${h.delta > 0 ? "text-emerald-600" : "text-destructive"}`}>
                    {h.delta > 0 ? "+" : ""}{h.delta} points
                  </span>
                  <span className="text-xs text-muted-foreground">{h.solde_avant} → {h.solde_apres}</span>
                </div>
                <p className="text-muted-foreground">{h.motif}</p>
                <p className="text-xs text-muted-foreground">{h.par} — {h.at ? new Date(h.at).toLocaleString("fr-FR") : ""}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function FileChip({ file, onRemove }) {
  const isImage = (file.content_type || "").startsWith("image/");
  const Icon = isImage ? ImageIcon : FileIcon;
  return (
    <div className="flex items-center gap-2 border border-border rounded-lg px-2.5 py-1.5 text-xs bg-secondary/50" data-testid={`client-file-${file.id}`}>
      <Icon size={13} className="text-muted-foreground shrink-0" />
      <a href={`${UPLOADS_BASE}${file.url}`} target="_blank" rel="noreferrer" className="truncate max-w-[140px] hover:underline">
        {file.original_name}
      </a>
      {onRemove && (
        <button onClick={onRemove} className="text-destructive shrink-0" data-testid={`client-file-remove-${file.id}`}>×</button>
      )}
    </div>
  );
}
